/**
 * SeoAuditCard — checklist semáforo de SEO / AI-readiness del sitio del
 * workspace (tab Overview del Brand Page).
 *
 * Qué hace: muestra el último audit que dejó el POST /scrape (checks de
 *   on-page + crawleabilidad de bots de IA, derivados de los SOPs de Doc/SEO).
 *   Al montar carga el último audit persistido; si el padre pasa `freshAudit`
 *   (recién llegado del scrape de esta sesión), ese gana.
 * Quién lo usa: components/brand/BrandTabOverview.tsx.
 */
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { SeoAudit, SeoCheckStatus } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../Card';

const STATUS_LABEL: Record<SeoCheckStatus, string> = {
  pass: 'OK',
  warn: 'Improve',
  fail: 'Fix',
};

const ScoreRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ScoreValue = styled.span<{ $score: number }>`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme, $score }) =>
    $score >= 80 ? theme.colors.success : $score >= 50 ? theme.colors.warning : theme.colors.error};
`;

const ScoreCaption = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CheckList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const CheckRow = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const StatusDot = styled.span<{ $status: SeoCheckStatus }>`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  background: ${({ theme, $status }) =>
    $status === 'pass' ? theme.colors.success : $status === 'warn' ? theme.colors.warning : theme.colors.error};
`;

const CheckText = styled.div`
  flex: 1;
  min-width: 0;
`;

const CheckLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const CheckDetail = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow-wrap: anywhere;
`;

const StatusTag = styled.span<{ $status: SeoCheckStatus }>`
  flex-shrink: 0;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme, $status }) =>
    $status === 'pass' ? theme.colors.success : $status === 'warn' ? theme.colors.warning : theme.colors.error};
`;

interface SeoAuditCardProps {
  workspaceId: string | undefined;
  /** Audit recién devuelto por el scrape de esta sesión — pisa el persistido. */
  freshAudit?: SeoAudit | null;
}

export function SeoAuditCard({ workspaceId, freshAudit }: SeoAuditCardProps) {
  const [audit, setAudit] = useState<SeoAudit | null>(null);

  // Al montar: último audit persistido del workspace (si existe).
  useEffect(() => {
    if (!workspaceId) return;
    let cancelled = false;
    apiClient
      .get<{ audit: SeoAudit | null }>(`/api/workspaces/${workspaceId}/seo-audit`)
      .then((data) => {
        if (!cancelled && data.audit) setAudit(data.audit);
      })
      .catch(() => { /* sin audit todavía — la card no se muestra */ });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  useEffect(() => {
    if (freshAudit) setAudit(freshAudit);
  }, [freshAudit]);

  // Sin audit no hay card: aparece después del primer "Analyze Website".
  if (!audit) return null;

  const auditDate = new Date(audit.fetchedAt);
  const dateLabel = Number.isNaN(auditDate.getTime()) ? '' : auditDate.toLocaleDateString();

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>SEO &amp; AI readiness</CardTitle>
          <CardDescription>
            How your website looks to Google and AI engines (ChatGPT, Claude, Perplexity, Gemini)
            {dateLabel ? ` — last checked ${dateLabel}` : ''}
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <ScoreRow>
          <ScoreValue $score={audit.score}>{audit.score}</ScoreValue>
          <ScoreCaption>/ 100 · {audit.url}</ScoreCaption>
        </ScoreRow>
        <CheckList>
          {audit.checks.map((check) => (
            <CheckRow key={check.id}>
              <StatusDot $status={check.status} aria-hidden="true" />
              <CheckText>
                <CheckLabel>{check.label}</CheckLabel>
                {check.detail && <CheckDetail>{check.detail}</CheckDetail>}
              </CheckText>
              <StatusTag $status={check.status}>{STATUS_LABEL[check.status]}</StatusTag>
            </CheckRow>
          ))}
        </CheckList>
      </CardContent>
    </Card>
  );
}
